import {
	coerceToPrimitiveArray,
	resolveFieldPath,
} from '../../../filterable-gallery/src/utils/filterEngine';

import type { CareersDataItem, ItemGroup } from '../types';

/** Bucket for items where `groupByPath` resolves to nothing, when the path DOES resolve for other items. */
const MISSING_GROUP_LABEL = 'Other';
const MISSING_GROUP_KEY = '__missing__';
const UNGROUPED_KEY = 'ungrouped';

/** Warn once per distinct unresolvable path rather than once per render (§5.5 rule 4). */
const warnedUnresolvablePaths = new Set<string>();

function warnUnresolvableGroupByPathOnce(groupByPath: string): void {
	if (warnedUnresolvablePaths.has(groupByPath)) return;
	warnedUnresolvablePaths.add(groupByPath);
	// eslint-disable-next-line no-console
	console.warn(
		`[careersGallery] groupByPath "${groupByPath}" resolved for no item — rendering a single ungrouped table.`,
	);
}

/**
 * Groups items by the first primitive value at `groupByPath`. Only the first
 * resolved value per item is used — a
 * `groupByPath` that resolves to multiple values (an array-spread path) puts
 * the item in the group for its first value only.
 *
 * Resolved groups are sorted alphabetically by label. The "Other" bucket for
 * items whose `groupByPath` did not resolve stays appended LAST, outside that
 * sort, so it never hides among the real groups.
 *
 * No `groupByPath` → one ungrouped group, `label: undefined` (no `H3`).
 * `groupByPath` resolves for no item at all → same ungrouped degrade, warned
 * once. `groupByPath` resolves for *some* items but not others → the
 * unresolved items get a defined "Other" bucket, appended last, distinct
 * from the fully-ungrouped case above.
 */
export function groupItemsByFieldPath(
	items: readonly CareersDataItem[],
	groupByPath: string | undefined,
): ItemGroup[] {
	if (!groupByPath) {
		return [{ key: UNGROUPED_KEY, label: undefined, items: [...items] }];
	}

	const groupsInOrder = new Map<string, CareersDataItem[]>();
	const missingGroupItems: CareersDataItem[] = [];
	let resolvedAtLeastOnce = false;

	for (const item of items) {
		const value = coerceToPrimitiveArray(resolveFieldPath(item, groupByPath))[0];
		if (value === undefined) {
			missingGroupItems.push(item);
			continue;
		}
		resolvedAtLeastOnce = true;
		if (!groupsInOrder.has(value)) groupsInOrder.set(value, []);
		groupsInOrder.get(value)?.push(item);
	}

	if (!resolvedAtLeastOnce) {
		warnUnresolvableGroupByPathOnce(groupByPath);
		return [{ key: UNGROUPED_KEY, label: undefined, items: [...items] }];
	}

	// The reference lists group headings alphabetically, not in the order the
	// data resource happens to return them.
	const groups: ItemGroup[] = Array.from(groupsInOrder.entries())
		.map(([value, groupItems]) => ({
			key: value,
			label: value,
			items: groupItems,
		}))
		.sort((a, b) => a.label.localeCompare(b.label));

	if (missingGroupItems.length > 0) {
		groups.push({ key: MISSING_GROUP_KEY, label: MISSING_GROUP_LABEL, items: missingGroupItems });
	}

	return groups;
}
